import React, { useState, useEffect, useRef } from 'react';
import { leadSourcesAPI } from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Check, ChevronsUpDown, Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function LeadSourceDropdown({ value, valueName, legacyValue, onChange }) {
  const [sources, setSources] = useState([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    leadSourcesAPI.list().then(res => setSources(res.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const displayValue = valueName || legacyValue || '';
  const filtered = sources.filter(s => s.name.toLowerCase().includes(search.toLowerCase()));
  const exactMatch = sources.find(s => s.name.toLowerCase() === search.trim().toLowerCase());

  const handleSelect = (source) => {
    onChange(source.id, source.name);
    setOpen(false);
    setSearch('');
  };

  const handleCreate = async () => {
    const name = search.trim();
    if (!name) return;
    setCreating(true);
    try {
      const res = await leadSourcesAPI.create({ name });
      const newSource = res.data;
      setSources(prev => [...prev, newSource].sort((a, b) => a.name.localeCompare(b.name)));
      onChange(newSource.id, newSource.name);
      setOpen(false);
      setSearch('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create lead source');
    } finally {
      setCreating(false);
    }
  };

  const handleClear = () => {
    onChange(null, '');
    setOpen(false);
    setSearch('');
  };

  return (
    <div className="relative" ref={ref} data-testid="lead-source-dropdown">
      <Button
        type="button"
        variant="outline"
        className="w-full justify-between text-sm font-normal h-9"
        onClick={() => setOpen(!open)}
        data-testid="lead-source-trigger"
      >
        <span className={displayValue ? '' : 'text-muted-foreground'}>
          {displayValue || 'Select lead source...'}
        </span>
        <ChevronsUpDown className="w-3.5 h-3.5 opacity-50 ml-2 shrink-0" />
      </Button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-popover border rounded-md shadow-lg max-h-64 overflow-hidden" data-testid="lead-source-popover">
          <div className="p-2 border-b">
            <Input
              placeholder="Search or create..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 text-sm"
              autoFocus
              data-testid="lead-source-search"
            />
          </div>
          <div className="max-h-44 overflow-y-auto p-1">
            {value && (
              <button
                className="w-full text-left px-2 py-1.5 text-xs text-muted-foreground hover:bg-accent rounded-sm"
                onClick={handleClear}
                data-testid="lead-source-clear"
              >
                Clear selection
              </button>
            )}
            {filtered.map(s => (
              <button
                key={s.id}
                className="w-full text-left px-2 py-1.5 text-sm hover:bg-accent rounded-sm flex items-center gap-2"
                onClick={() => handleSelect(s)}
                data-testid={`lead-source-option-${s.id}`}
              >
                {value === s.id && <Check className="w-3.5 h-3.5 text-primary shrink-0" />}
                <span className={value === s.id ? 'font-medium' : ''}>{s.name}</span>
              </button>
            ))}
            {filtered.length === 0 && !search.trim() && (
              <p className="text-xs text-muted-foreground px-2 py-3 text-center">No lead sources yet</p>
            )}
            {search.trim() && !exactMatch && (
              <button
                className="w-full text-left px-2 py-1.5 text-sm hover:bg-accent rounded-sm flex items-center gap-2 text-primary"
                onClick={handleCreate}
                disabled={creating}
                data-testid="lead-source-create"
              >
                {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                Create "{search.trim()}"
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
